import { useState } from "react";
import { Search } from "lucide-react";
import { Button } from "@/components/ui/button";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Input } from "@/components/ui/input";
import type { ListingFilters } from "@/lib/listings";
import { useLanguage } from "@/contexts/LanguageContext";

const NEIGHBORHOODS = [
  "Barra da Tijuca",
  "Recreio dos Bandeirantes",
  "Copacabana",
  "Ipanema",
  "Leblon",
  "Botafogo",
  "Flamengo",
  "Laranjeiras",
  "Tijuca",
  "Alto da Boa Vista",
];

const PROPERTY_TYPES = [
  { value: "apartamento", label: "Apartamento" },
  { value: "cobertura", label: "Cobertura" },
  { value: "casa", label: "Casa" },
];

interface SearchSectionProps {
  onSearch: (filters: ListingFilters) => void;
}

const SearchSection = ({ onSearch }: SearchSectionProps) => {
  const { t } = useLanguage();
  const [neighborhood, setNeighborhood] = useState("");
  const [propertyType, setPropertyType] = useState("");
  const [bedroomsMin, setBedroomsMin] = useState("");
  const [garageMin, setGarageMin] = useState("");
  const [priceMax, setPriceMax] = useState("");
  const [areaMin, setAreaMin] = useState("");
  const [listingCode, setListingCode] = useState("");
  const [sortBy, setSortBy] = useState("");

  const handleSearch = () => {
    onSearch({
      neighborhood: neighborhood || undefined,
      property_type: propertyType || undefined,
      bedrooms_min: bedroomsMin ? Number(bedroomsMin) : undefined,
      garage_min: garageMin ? Number(garageMin) : undefined,
      price_max: priceMax ? Number(priceMax) : undefined,
      area_min: areaMin ? Number(areaMin) : undefined,
      listing_code: listingCode || undefined,
      sort_by: sortBy || undefined,
    });
  };

  return (
    <section id="buscar" className="relative -mt-12 z-20 container mx-auto px-4">
      <div className="bg-card rounded-lg shadow-2xl p-6 md:p-8 border border-border">
        <h2 className="text-2xl font-display font-bold text-foreground mb-6 text-center">
          {t("search.title")}
        </h2>
        <div className="grid grid-cols-1 md:grid-cols-3 lg:grid-cols-4 gap-4">
          <Input
            type="text"
            placeholder="Código do imóvel"
            value={listingCode}
            onChange={(e) => setListingCode(e.target.value)}
            className="font-body"
          />

          <Select value={neighborhood} onValueChange={setNeighborhood}>
            <SelectTrigger>
              <SelectValue placeholder={t("search.neighborhood")} />
            </SelectTrigger>
            <SelectContent>
              {NEIGHBORHOODS.map((n) => (
                <SelectItem key={n} value={n}>{n}</SelectItem>
              ))}
            </SelectContent>
          </Select>

          <Select value={propertyType} onValueChange={setPropertyType}>
            <SelectTrigger>
              <SelectValue placeholder={t("search.type")} />
            </SelectTrigger>
            <SelectContent>
              {PROPERTY_TYPES.map((t) => (
                <SelectItem key={t.value} value={t.value}>{t.label}</SelectItem>
              ))}
            </SelectContent>
          </Select>

          <Select value={bedroomsMin} onValueChange={setBedroomsMin}>
            <SelectTrigger>
              <SelectValue placeholder={t("search.bedrooms")} />
            </SelectTrigger>
            <SelectContent>
              {[1, 2, 3, 4, 5].map((n) => (
                <SelectItem key={n} value={String(n)}>{n}+ {t("search.bedroomsPlus")}</SelectItem>
              ))}
            </SelectContent>
          </Select>

          <Select value={garageMin} onValueChange={setGarageMin}>
            <SelectTrigger>
              <SelectValue placeholder={t("search.garage")} />
            </SelectTrigger>
            <SelectContent>
              {[1, 2, 3, 4].map((n) => (
                <SelectItem key={n} value={String(n)}>{n}+ {t("search.garagePlus")}</SelectItem>
              ))}
            </SelectContent>
          </Select>

          <Input
            type="number"
            placeholder={t("search.areaMin")}
            value={areaMin}
            onChange={(e) => setAreaMin(e.target.value)}
            className="font-body"
          />

          <Input
            type="number"
            placeholder={t("search.priceMax")}
            value={priceMax}
            onChange={(e) => setPriceMax(e.target.value)}
            className="font-body"
          />

          <Select value={sortBy} onValueChange={setSortBy}>
            <SelectTrigger>
              <SelectValue placeholder="Ordenar por" />
            </SelectTrigger>
            <SelectContent>
              <SelectItem value="default">Destaques primeiro</SelectItem>
              <SelectItem value="price_per_m2_asc">Menor valor/m²</SelectItem>
            </SelectContent>
          </Select>
        </div>
        <div className="mt-6 flex justify-center">
          <Button variant="gold" size="lg" onClick={handleSearch} className="min-w-[200px]">
            <Search className="h-5 w-5" />
            {t("search.button")}
          </Button>
        </div>
      </div>
    </section>
  );
};

export default SearchSection;
